import { Plus, X } from 'lucide-react'

import { BotonDeIcono, clases } from '../../componentes/ui'
import type { Campo } from '../esquema'
import { useValor } from '../Formulario'
import { claseDeEntrada, Envoltura } from './Controles'

/**
 * Un horario semanal (la agenda y cada recurso, CLAUDE.md §33): se guarda como `[{ dia, desde, hasta }]`
 * y se edita día a día, con uno o varios tramos («de 9 a 13 y de 15 a 19»). Un día sin tramos está
 * cerrado. Lo que vale lo decide el núcleo (`revisarHorario`): si dos tramos se pisan, lo dice al guardar.
 */
type Tramo = { dia: number; desde: string; hasta: string }

// La semana del negocio empieza en lunes.
const DIAS: Array<[number, string]> = [
  [1, 'Lunes'],
  [2, 'Martes'],
  [3, 'Miércoles'],
  [4, 'Jueves'],
  [5, 'Viernes'],
  [6, 'Sábado'],
  [0, 'Domingo'],
]

const esTramo = (t: unknown): t is Tramo => Boolean(t) && typeof t === 'object' && typeof (t as Tramo).dia === 'number' && typeof (t as Tramo).desde === 'string' && typeof (t as Tramo).hasta === 'string'

export const Horario = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<unknown>(ruta)
  const tramos = (Array.isArray(valor) ? valor : []).filter(esTramo)
  const guardar = (nuevos: Tramo[]) => cambiar(nuevos.length ? nuevos : null)
  const delDia = (dia: number) => tramos.map((t, i) => ({ t, i })).filter(({ t }) => t.dia === dia)
  const cambiarTramo = (indice: number, cambio: Partial<Tramo>) => guardar(tramos.map((t, i) => (i === indice ? { ...t, ...cambio } : t)))
  const quitar = (indice: number) => guardar(tramos.filter((_, i) => i !== indice))
  const anadir = (dia: number) => {
    const ultimo = delDia(dia).at(-1)?.t
    guardar([...tramos, ultimo ? { dia, desde: ultimo.hasta, hasta: ultimo.hasta < '20:00' ? '20:00' : '23:00' } : { dia, desde: '09:00', hasta: '18:00' }])
  }
  const copiarALaSemana = (dia: number) => {
    const modelo = delDia(dia).map(({ t }) => t)
    const laborables = [1, 2, 3, 4, 5].filter((d) => d !== dia)
    guardar([...tramos.filter((t) => !laborables.includes(t.dia)), ...laborables.flatMap((d) => modelo.map((t) => ({ ...t, dia: d })))].sort((a, b) => a.dia - b.dia || a.desde.localeCompare(b.desde)))
  }

  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div className="flex flex-col divide-y divide-linea rounded-caja border border-linea" id={ruta}>
        {DIAS.map(([dia, nombre]) => {
          const suyos = delDia(dia)
          return (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2" key={dia}>
              <span className="w-24 shrink-0 text-[13px] font-medium">{nombre}</span>
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                {suyos.length === 0 && <span className="text-[13px] text-tenue">Cerrado</span>}
                {suyos.map(({ t, i }) => (
                  <span className="inline-flex items-center gap-1" key={i}>
                    <input aria-label={`${nombre}: desde`} className={clases(claseDeEntrada(), 'h-8 w-[6.5rem] px-2')} disabled={soloLectura} onChange={(e) => cambiarTramo(i, { desde: e.target.value })} type="time" value={t.desde} />
                    <span className="text-tenue">a</span>
                    <input aria-label={`${nombre}: hasta`} className={clases(claseDeEntrada(), 'h-8 w-[6.5rem] px-2')} disabled={soloLectura} onChange={(e) => cambiarTramo(i, { hasta: e.target.value })} type="time" value={t.hasta} />
                    {!soloLectura && (
                      <BotonDeIcono etiqueta={`Quitar el tramo de ${nombre.toLowerCase()} de ${t.desde} a ${t.hasta}`} onClick={() => quitar(i)} pequeno>
                        <X />
                      </BotonDeIcono>
                    )}
                  </span>
                ))}
              </div>
              {!soloLectura && (
                <span className="flex gap-1">
                  <BotonDeIcono etiqueta={`Añadir un tramo el ${nombre.toLowerCase()}`} onClick={() => anadir(dia)} pequeno>
                    <Plus />
                  </BotonDeIcono>
                  {suyos.length > 0 && dia >= 1 && dia <= 5 && (
                    <button className="h-8 rounded-boton px-2 text-[12px] text-tenue hover:bg-papel-3 hover:text-tinta" onClick={() => copiarALaSemana(dia)} type="button">
                      Igual de lunes a viernes
                    </button>
                  )}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </Envoltura>
  )
}
